import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  apiErrorMessage,
  getInstalledIntegrations,
  getIntegrationCatalog,
  IntegrationProvider,
  TenantIntegration,
} from "../../../helpers/integrations_helper";

/**
 * Vitrine Hub — catálogo + conectados (DOC 11 § 8 · fatia #6.0).
 * Por quê: Carla encontra a loja sem misturar Tokens/Webhooks de desenvolvedor.
 */
const IntegrationsHub = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";

  document.title = t("integrations.documentTitle");

  const helpHref = helpUrlForRoute("/integrations", locale);

  const [catalog, setCatalog] = useState<IntegrationProvider[]>([]);
  const [installed, setInstalled] = useState<TenantIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cat, inst] = await Promise.all([
        getIntegrationCatalog(),
        getInstalledIntegrations(),
      ]);
      if (!cat?.success || !inst?.success) {
        setError(t("integrations.error.load"));
        return;
      }
      setCatalog(cat.data || []);
      setInstalled(inst.data || []);
    } catch (e) {
      setError(apiErrorMessage(e, t("integrations.error.load")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const vtexInstalled = installed.find((i) => i.provider_slug === "vtex");
  const shopifyInstalled = installed.find((i) => i.provider_slug === "shopify");
  const uappiInstalled = installed.find((i) => i.provider_slug === "uappi");

  const detailPath = (slug: string) => `/integrations/${slug}`;

  const installedFor = (slug: string) => {
    if (slug === "vtex") return vtexInstalled;
    if (slug === "shopify") return shopifyInstalled;
    if (slug === "uappi") return uappiInstalled;
    return undefined;
  };

  const ecommerceSlugs = ["vtex", "shopify", "uappi"];
  // Só uma loja ecommerce.sync por conta — aviso evita Carla tentar “conectar as três”.
  const activeEcommerce = installed.find(
    (i) => i.enabled && i.capability === "ecommerce.sync"
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("integrations.title")}
            pageTitle={t("menu.integrations")}
          />
          <div className="d-flex justify-content-between align-items-start mb-3 gap-2 flex-wrap">
            <DismissiblePageTip tipId="integrations-hub">
              {t("integrations.tip")}
            </DismissiblePageTip>
            <PageHelpLink href={helpHref} label={t("integrations.help")} />
          </div>

          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {activeEcommerce && (
            <Alert color="info" className="mb-3">
              {t("integrations.oneStoreHint", {
                store: (activeEcommerce.provider_slug || "").toUpperCase(),
              })}{" "}
              <Link
                to={detailPath(activeEcommerce.provider_slug)}
                className="alert-link"
              >
                {t("integrations.manageCurrentStore")}
              </Link>
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <Row>
              <Col lg={8}>
                <Card>
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("integrations.catalogTitle")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    {catalog.length === 0 ? (
                      <p className="text-muted mb-0">
                        {t("integrations.emptyCatalog")}
                      </p>
                    ) : (
                      catalog.map((p) => {
                        const mine = installedFor(p.slug);
                        const blockedByOther =
                          ecommerceSlugs.includes(p.slug) &&
                          !!activeEcommerce &&
                          activeEcommerce.provider_slug !== p.slug;
                        return (
                        <div
                          key={p.slug}
                          className="d-flex flex-wrap align-items-center justify-content-between border rounded p-3 mb-2 gap-2"
                        >
                          <div>
                            <h6 className="mb-1">{p.name}</h6>
                            <p className="text-muted mb-0 small">
                              {t(`integrations.category.${p.category}`, {
                                defaultValue: p.category,
                              })}
                            </p>
                            {blockedByOther && (
                              <p className="text-muted mb-0 small mt-1">
                                {t("integrations.disconnectFirst")}
                              </p>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {ecommerceSlugs.includes(p.slug) ? (
                              mine ? (
                                <>
                                  <Badge color="success">
                                    {t("integrations.connected")}
                                  </Badge>
                                  <Button
                                    color="primary"
                                    outline
                                    size="sm"
                                    tag={Link}
                                    to={detailPath(p.slug)}
                                  >
                                    {t("integrations.manage")}
                                  </Button>
                                </>
                              ) : blockedByOther ? (
                                <Button
                                  color="secondary"
                                  outline
                                  size="sm"
                                  tag={Link}
                                  to={detailPath(activeEcommerce!.provider_slug)}
                                >
                                  {t("integrations.switchStoreCta")}
                                </Button>
                              ) : (
                                <Button
                                  color="primary"
                                  size="sm"
                                  tag={Link}
                                  to={detailPath(p.slug)}
                                >
                                  {t("integrations.connect")}
                                </Button>
                              )
                            ) : (
                              <Badge color="secondary">
                                {t("integrations.comingSoon")}
                              </Badge>
                            )}
                          </div>
                        </div>
                        );
                      })
                    )}
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="mb-3">
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("integrations.sitePixelCard.title")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted mb-3">
                      {t("integrations.sitePixelCard.description")}
                    </p>
                    <Button
                      color="primary"
                      size="sm"
                      tag={Link}
                      to="/integrations/site-pixel"
                    >
                      {t("integrations.sitePixelCard.manage")}
                    </Button>
                  </CardBody>
                </Card>
                <Card>
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("integrations.connectedTitle")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    {installed.length === 0 ? (
                      <div>
                        <p className="text-muted mb-2">
                          {t("integrations.emptyConnected")}
                        </p>
                        <Button
                          color="primary"
                          size="sm"
                          tag={Link}
                          to="/integrations"
                        >
                          {t("integrations.connectStoreCta")}
                        </Button>
                      </div>
                    ) : (
                      <ul className="list-unstyled mb-0">
                        {installed.map((i) => (
                          <li key={i.id} className="mb-2">
                            <Link to={`/integrations/${i.provider_slug}`}>
                              {i.provider_slug.toUpperCase()}
                            </Link>
                            <div className="small text-muted">
                              {i.account_name}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default IntegrationsHub;
